import { useEffect, useRef, useState } from "react";
import { ReactComponent as SendLogo } from "../../Assets/arrow-up-solid.svg";
import { ReactComponent as StopIcon } from "../../Assets/circle-stop-regular.svg";
import { activateOnKey } from "../../Helper/a11y";
import { useConfigStore } from "../../Stores/Config";
import { useTicketStore } from "../../Stores/Ticket";
import { DeleteIcon, MicIcon } from "../AnimatedIcons/AnimatedIcons";
import AudioMessagePreview from "./AudioMessagePreview";
import "./AudioRecorderUpload.scss";

interface AudioRecorderUploadProps {
  onAudioRecordingStart?: () => void;
  onAudioRecordingStop?: () => void;
  onSend?: (attachment: { url: string; name: string; type: string }) => void;
}

const MAX_RECORDING_TIME = 120000; // 2 minutes in milliseconds

const AudioRecorderUpload = ({
  onAudioRecordingStart,
  onAudioRecordingStop,
  onSend,
}: AudioRecorderUploadProps) => {
  const config = useConfigStore();
  const maxRecordingTime = config?.config?.audioMaxLength ?? MAX_RECORDING_TIME;

  const [isRecording, setIsRecording] = useState(false);
  // Bridges the gap between mediaRecorder.stop() (synchronous, isRecording
  // flips false) and its async `onstop` that produces the blob/audioURL.
  // Without it the recorder briefly falls back to its idle mic while the
  // composer still shows the audio layout — the "broke while loading" flash.
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [audioURL, setAudioURL] = useState<string | null>(null);
  const [recordedDurationMs, setRecordedDurationMs] = useState(0);
  const [isSending, setIsSending] = useState(false); // For sending file
  const [rawFile, setRawFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const isDeletingRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Wall-clock start of the current recording, for an accurate final length
  // (the blob's own metadata duration is unreliable for webm in Chrome).
  const recordingStartRef = useRef<number>(0);

  // Live waveform visualization driven by the actual mic input.
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const stopVisualizer = () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    analyserRef.current = null;
  };

  // Stop everything if the component unmounts mid-recording.
  useEffect(() => {
    return () => {
      stopVisualizer();
    };
  }, []);

  // Animated waveform: bars peak in the center and taper to dots at the
  // edges (matching the reference). Bar heights follow the live mic level
  // when an analyser is available, otherwise a gentle idle shimmer.
  useEffect(() => {
    if (!isRecording) {
      return;
    }
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return;
    }

    const analyser = analyserRef.current;
    const bufferLength = analyser ? analyser.frequencyBinCount : 64;
    const dataArray = new Uint8Array(bufferLength);
    const barColor = getComputedStyle(canvas).color || "rgb(17, 17, 17)";

    const draw = () => {
      animationFrameRef.current = requestAnimationFrame(draw);

      const dpr = window.devicePixelRatio || 1;
      const cssW = canvas.clientWidth;
      const cssH = canvas.clientHeight;
      if (cssW === 0 || cssH === 0) {
        return;
      }
      if (canvas.width !== Math.round(cssW * dpr)) {
        canvas.width = Math.round(cssW * dpr);
      }
      if (canvas.height !== Math.round(cssH * dpr)) {
        canvas.height = Math.round(cssH * dpr);
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, cssW, cssH);

      if (analyser) {
        analyser.getByteFrequencyData(dataArray);
      }
      const time = performance.now() / 1000;

      const barWidth = 2.5;
      const gap = 3.5;
      const step = barWidth + gap;
      const barCount = Math.max(1, Math.floor(cssW / step));
      const centerY = cssH / 2;
      const minBar = 2.5;
      const offsetX = (cssW - (barCount * step - gap)) / 2;

      for (let i = 0; i < barCount; i++) {
        const t = barCount > 1 ? i / (barCount - 1) : 0.5;
        const envelope = Math.pow(Math.sin(t * Math.PI), 0.85);

        let level;
        if (analyser) {
          const bin = Math.floor((i / barCount) * (bufferLength * 0.6));
          level = dataArray[bin] / 255;
        } else {
          level = 0.25 + 0.2 * Math.abs(Math.sin(time * 3 + i * 0.6));
        }

        let height = minBar + level * (cssH - minBar) * envelope;
        height = Math.max(minBar, Math.min(cssH, height));

        const x = offsetX + i * step;
        const y = centerY - height / 2;
        const radius = Math.min(barWidth / 2, height / 2);

        ctx.globalAlpha = 0.2 + 0.8 * envelope;
        ctx.fillStyle = barColor;
        if ((ctx as any).roundRect) {
          ctx.beginPath();
          (ctx as any).roundRect(x, y, barWidth, height, radius);
          ctx.fill();
        } else {
          ctx.fillRect(x, y, barWidth, height);
        }
      }
      ctx.globalAlpha = 1;
    };

    draw();

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [isRecording]);

  const startRecording = async () => {
    try {
      // NB: do NOT flip the composer into audio-layout yet. onAudioRecordingStart
      // fires only once recording actually begins (below) — firing it here, before
      // the async getUserMedia resolves, collapses the composer to a lone mic while
      // the mic-permission prompt / stream acquisition is pending (looks broken).
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported("audio/mp4")
        ? "audio/mp4"
        : MediaRecorder.isTypeSupported("audio/webm")
          ? "audio/webm"
          : "";

      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      // Tap the live stream with an analyser so the waveform reacts to the
      // user's voice. Purely visual — failure here must not block recording.
      try {
        const AudioCtx =
          window.AudioContext || (window as any).webkitAudioContext;
        if (AudioCtx) {
          const audioCtx = new AudioCtx();
          const source = audioCtx.createMediaStreamSource(stream);
          const analyser = audioCtx.createAnalyser();
          analyser.fftSize = 256;
          analyser.smoothingTimeConstant = 0.75;
          source.connect(analyser);
          audioContextRef.current = audioCtx;
          analyserRef.current = analyser;
        }
      } catch (_) {
        // Visualization is optional.
      }

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        if (isDeletingRef.current) {
          isDeletingRef.current = false;
          setIsFinalizing(false);
          return;
        }

        const recordedType = mediaRecorderRef.current?.mimeType || "audio/wav";
        const audioBlob = new Blob(audioChunksRef.current, {
          type: recordedType,
        });
        setAudioURL(URL.createObjectURL(audioBlob));

        // No client-side transcoding: hand the raw recording off as-is. The
        // server transcodes it to MP3 on upload (see uploadAudioRecording).
        // mimeType may include codec params (e.g. "audio/webm;codecs=opus"),
        // so match on the base type to pick the file extension.
        const baseMimeType = recordedType.split(";")[0].trim();
        const ext =
          baseMimeType === "audio/webm"
            ? "webm"
            : baseMimeType === "audio/mp4"
              ? "m4a"
              : "wav";

        const rawAudioFile = new File([audioBlob], `recorded-audio.${ext}`, {
          type: baseMimeType || "audio/wav",
        });

        setRawFile(rawAudioFile);
        audioChunksRef.current = [];
        setIsFinalizing(false);
      };

      mediaRecorder.start();
      // Only now that recording has truly begun do we switch the composer into
      // audio-layout — so it transforms straight into the waveform view with no
      // intermediate lone-mic collapse.
      onAudioRecordingStart?.();
      setIsRecording(true);
      setIsFinalizing(false);
      setRecordingTime(0);
      recordingStartRef.current = performance.now();

      // Start the timer
      timerRef.current = setInterval(() => {
        setRecordingTime((prevTime) => {
          const newTime = prevTime + 1000;
          if (newTime >= maxRecordingTime) {
            // Stop recording when we reach the max time
            stopRecording({ shouldDelete: false });
            return 0;
          }
          return newTime;
        });
      }, 1000);
    } catch (error: any) {
      onAudioRecordingStop?.();
      if (
        error.name === "NotAllowedError" ||
        error.name === "PermissionDeniedError"
      ) {
        alert(
          error.message ??
            "Microphone access is denied. Please enable microphone permissions to record audio.",
        );
      } else {
        console.error("Error accessing user's microphone:", error);
      }
    }
  };

  const stopRecording = (args: { shouldDelete?: boolean }) => {
    try {
      if (mediaRecorderRef.current) {
        if (args.shouldDelete) {
          isDeletingRef.current = true;
        } else {
          // Show the finalizing indicator until `onstop` hands back the blob,
          // and freeze the recorded length now for the preview readout.
          setIsFinalizing(true);
          setRecordedDurationMs(
            Math.max(0, performance.now() - recordingStartRef.current),
          );
        }

        mediaRecorderRef.current.stop();
        setIsRecording(false);
        mediaRecorderRef.current.stream
          .getTracks()
          .forEach((track) => track.stop());
      }
    } catch (_) {}

    stopVisualizer();

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (args.shouldDelete) {
      setIsFinalizing(false);
      setAudioURL(null);
      setRawFile(null);
      onAudioRecordingStop?.();

      setTimeout(() => {
        isDeletingRef.current = false;
      }, 100);
    }
  };

  const handleSend = async () => {
    if (isRecording) {
      stopRecording({ shouldDelete: false });
    }

    if (rawFile) {
      setIsSending(true); // Start sending process
      await sendFile();
    } else {
      setIsProcessing(true);
    }
  };

  useEffect(() => {
    if (rawFile && isProcessing) {
      sendFile();
    }
  }, [rawFile, isProcessing]);

  const sendFile = async () => {
    if (!rawFile) {
      setIsProcessing(false);
      setIsSending(false);
      return;
    }

    setIsSending(true);
    try {
      // Upload the raw recording; the server transcodes it to MP3 and returns
      // the stored attachment descriptor we forward to the conversation.
      const attachment = await useTicketStore
        .getState()
        .uploadAudioRecording(rawFile);
      onSend?.(attachment);
    } catch (error: any) {
      console.error("Audio upload failed:", error);
      alert(error?.message ?? "Audio upload failed. Please try again.");
    } finally {
      setAudioURL(null);
      setRawFile(null);
      setIsProcessing(false);
      setIsSending(false); // Reset sending state
      setIsFinalizing(false);
      onAudioRecordingStop?.();
    }
  };

  if (!config?.config?.audio) {
    return <></>;
  }

  if (audioURL) {
    return (
      <div className="audio-recorder-upload audio-recorder-upload--recording-paused">
        <AudioMessagePreview
          src={audioURL}
          durationMs={recordedDurationMs}
          isSending={isSending}
          onSend={handleSend}
          onDelete={() => {
            stopRecording({ shouldDelete: true });
          }}
        />
      </div>
    );
  }

  // The clip is being finalized (recorder stopped, blob not ready yet). Hold
  // the audio layout with a calm indicator instead of dropping back to the
  // idle mic for a frame.
  if (isFinalizing) {
    return (
      <div className="audio-recorder-upload audio-recorder-upload--finalizing">
        <div className="audio-spinner" />
      </div>
    );
  }

  if (isRecording) {
    const totalSeconds = Math.floor(recordingTime / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    const maxMinutes = Math.floor(maxRecordingTime / 60000);
    const maxSeconds = Math.floor((maxRecordingTime % 60000) / 1000);
    const timeDisplay = `${minutes}:${seconds
      .toString()
      .padStart(2, "0")}/${maxMinutes}:${maxSeconds
      .toString()
      .padStart(2, "0")}`;

    return (
      <div className="audio-recorder-upload audio-recorder-upload--recording">
        <canvas ref={canvasRef} className="audio-recorder-waveform" />
        <div className="audio-recorder-controls">
          <DeleteIcon
            className="audio-recorder-icon audio-recorder-icon--trash"
            role="button"
            tabIndex={0}
            aria-label={config.translateTextWithFallback(
              "deleteRecording",
              "Delete recording",
            )}
            onClick={() => {
              stopRecording({ shouldDelete: true });
            }}
            onKeyDown={activateOnKey(() => {
              stopRecording({ shouldDelete: true });
            })}
          />
          <div className="audio-recorder-controls-right">
            <div className="recording-timer">{timeDisplay}</div>
            <StopIcon
              className="audio-recorder-icon audio-pause-icon recording-pulse"
              role="button"
              tabIndex={0}
              aria-label={config.translateTextWithFallback(
                "stopRecording",
                "Stop recording",
              )}
              onClick={() => {
                stopRecording({ shouldDelete: false });
              }}
              onKeyDown={activateOnKey(() => {
                stopRecording({ shouldDelete: false });
              })}
            />
            <div
              onClick={handleSend}
              onKeyDown={activateOnKey(handleSend)}
              role="button"
              tabIndex={0}
              aria-label={config.translateTextWithFallback(
                "sendRecording",
                "Send recording",
              )}
              className="send-button"
            >
              <SendLogo />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="audio-recorder-upload">
      {!isRecording && (
        <MicIcon
          className="audio-recorder-icon"
          role="button"
          tabIndex={0}
          aria-label={config.translateTextWithFallback(
            "startAudioRecording",
            "Start audio recording",
          )}
          onClick={startRecording}
          onKeyDown={activateOnKey(startRecording)}
        />
      )}
    </div>
  );
};

export default AudioRecorderUpload;
